import React, { useState, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import styles from './AddBankAccount.module.css';
import { useModal } from '../../../../helpers/hooks/modalContext';
import {
  createPaymentOption,
  getBanks,
  getPresignedUrl,
  uploadToS3,
  getPadTerms,
  getTenantDetails,
  authPayment,
} from '../../../../auth/_requests';
import { useAuth } from '../../../../auth/Auth';
import { motion } from 'framer-motion';
import { ChevronLeft, X, ArrowUpFromLine, FileText, Trash } from 'lucide-react';
import { showToast } from '../../../../helpers/utils/showToast';
import { usePaymentOptions } from '../../../hooks/PaymentOptionsContext';
import { Bank } from '../../../../helpers/utils/types';
import { Checkbox } from '../../../UI/Checkbox/Checkbox';
import { formatDateToDisplayUTCLong } from '../../../../helpers/utils/utils';
import { sanitizeHtml } from '../../../../helpers/utils/sanitize';
import { trackPaymentMethodAdded } from '../../../../helpers/utils/analytics';
import canadaBankCheque from '../../../../assets/pages/payment/canada_bank_cheque.jpg';

interface BankAccountFormValues {
  accountName: string;
  transitNumber: string;
  institutionNumber: string;
  accountNumber: string;
  bankName: string;
  voidChequePath: string;
}

const getValidationSchema = (selectedBank: Bank | null) => {
  return Yup.object({
    accountName: Yup.string()
      .max(255, 'Account name must be at most 255 characters')
      .required('Account name is required')
      .test('hasText', 'Account name cannot be only spaces', (value) => {
        return value ? /\S/.test(value) : false;
      }),
    transitNumber: Yup.string()
      .required('Transit number is required')
      .matches(/^\d+$/, 'Transit number must contain only numbers')
      .test('length', 'Transit number must be exactly 5 characters', (value) => {
        if (!value) return false;
        return value.length === 5;
      })
      .test(
        'fixedValue',
        `Transit number must be ${selectedBank?.transitNumberFixedValue}`,
        (value) => {
          if (!selectedBank || !selectedBank.transitNumberFixedValue) return true;
          return value === selectedBank.transitNumberFixedValue;
        }
      )
      .test(
        'startsWith',
        `Transit number must start with ${selectedBank?.transitNumberStartsWith}`,
        (value) => {
          if (!selectedBank || !selectedBank.transitNumberStartsWith) return true;
          return value?.startsWith(selectedBank.transitNumberStartsWith) || false;
        }
      ),
    institutionNumber: Yup.string()
      .test('required', 'Institution number is required', (value) => {
        if (selectedBank?.name === 'Other') return true; // Optional for "Other"
        return !!value;
      })
      .matches(/^\d+$/, 'Institution number must contain only numbers')
      .test('length', 'Institution number must be exactly 3 characters', (value) => {
        if (selectedBank?.name === 'Other') return true; // Optional for "Other"
        if (!value) return false;
        return value.length === 3;
      }),
    accountNumber: Yup.string()
      .required('Account number is required')
      .matches(/^\d+$/, 'Account number must contain only numbers')
      .test(
        'minLength',
        selectedBank?.accountNumberMinLength
          ? `Account number must be at least ${selectedBank.accountNumberMinLength} digits`
          : 'Account number length is invalid',
        (value) => {
          if (!selectedBank || !value || !selectedBank.accountNumberMinLength) return true;
          return value.length >= selectedBank.accountNumberMinLength;
        }
      )
      .test(
        'maxLength',
        selectedBank?.accountNumberMaxLength
          ? `Account number must be at most ${selectedBank.accountNumberMaxLength} digits`
          : 'Account number length is invalid',
        (value) => {
          if (!selectedBank || !value || !selectedBank.accountNumberMaxLength) return true;
          return value.length <= selectedBank.accountNumberMaxLength;
        }
      )
      .test(
        'fixedLengths',
        selectedBank &&
          selectedBank.accountNumberFixedLengths &&
          selectedBank.accountNumberFixedLengths.values.length > 0
          ? `Account number must be exactly ${selectedBank.accountNumberFixedLengths.values.join(' or ')} digits`
          : 'Account number length is invalid',
        (value) => {
          if (!selectedBank || !value) return true;
          if (
            !selectedBank.accountNumberFixedLengths?.values ||
            selectedBank.accountNumberFixedLengths.values.length === 0
          )
            return true;
          return selectedBank.accountNumberFixedLengths.values.includes(value.length);
        }
      ),
    bankName: Yup.string()
      .max(255, 'Bank name must be at most 255 characters')
      .required('Bank name is required'),
    voidChequePath: Yup.string().max(500, 'Void cheque path must be at most 500 characters'),
  });
};

const AddBankAccount = () => {
  const navigate = useNavigate();
  const { setModal, modalState } = useModal();
  const payRentSetupFlow = modalState?.payRentSetupFlow === true;
  const { auth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState('');
  const [fileName, setFileName] = useState('');
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [selectedBank, setSelectedBank] = useState<Bank | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { refreshPaymentOptions } = usePaymentOptions();
  const [currentStep, setCurrentStep] = useState<'form' | 'terms'>('form');
  const [termsContent, setTermsContent] = useState<string>('');
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const termsContentRef = useRef<HTMLDivElement | null>(null);

  // File validation constants
  const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const allowedTypes = [...allowedImageTypes, 'application/pdf'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

  useEffect(() => {
    const fetchBanks = async () => {
      try {
        setLoadingBanks(true);
        const response = await getBanks(auth?.data.token);
        setBanks(response.data || []);
      } catch (error) {
        console.error('Error fetching banks:', error);
        showToast({
          message: 'Failed to load banks. Please try again.',
          type: 'error',
        });
      } finally {
        setLoadingBanks(false);
      }
    };
    fetchBanks();
  }, [auth?.data.token]);

  // Check scroll position when terms content changes or step changes
  useEffect(() => {
    if (currentStep === 'terms' && termsContent && !loadingTerms) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        checkScrollPosition();
      }, 200);
    }
  }, [currentStep, termsContent, loadingTerms]);

  const handleBankChange = (bankNameId: string) => {
    const bank = banks.find((b) => b.bankNameId === bankNameId);
    setSelectedBank(bank || null);

    if (bank) {
      // Auto-populate institution number if available
      if (bank.institutionNumber) {
        formik.setFieldValue('institutionNumber', bank.institutionNumber);
      } else {
        formik.setFieldValue('institutionNumber', '');
      }

      // Auto-populate transit number if it has a fixed value
      if (bank.transitNumberFixedValue) {
        formik.setFieldValue('transitNumber', bank.transitNumberFixedValue);
      } else {
        formik.setFieldValue('transitNumber', '');
      }

      // Update validation schema and bank name
      formik.setFieldValue('bankName', bank.name);

      // Clear account number to force revalidation with new rules
      const currentAccountNumber = formik.values.accountNumber;
      formik.setFieldValue('accountNumber', '');
      setTimeout(() => {
        formik.setFieldValue('accountNumber', currentAccountNumber);
        formik.validateField('accountNumber');
        formik.validateField('transitNumber');
      }, 0);
    } else {
      formik.setFieldValue('bankName', '');
      formik.setFieldValue('institutionNumber', '');
      formik.setFieldValue('transitNumber', '');
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type - only check mime type
    if (!allowedTypes.includes(file.type)) {
      showToast({
        message: 'Please upload a valid file (PDF or Image format only)',
        type: 'error',
      });
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      const maxSizeMB = MAX_FILE_SIZE / (1024 * 1024);
      showToast({
        message: `File size exceeds the maximum limit of ${maxSizeMB}MB`,
        type: 'error',
      });
      return;
    }

    try {
      setIsUploading(true);
      const { uploadUrl, url } = await getPresignedUrl(
        file.name,
        file.type,
        auth?.data.token,
        'tenant-bank-accounts'
      );
      await uploadToS3(file, uploadUrl);

      setFileName(file.name);
      setSelectedFile(url);
      formik.setFieldValue('voidChequePath', url);
    } catch (error) {
      console.error('Error uploading file:', error);
      showToast({
        message: 'Failed to upload void cheque. Please try again.',
        type: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const formik = useFormik<BankAccountFormValues>({
    initialValues: {
      accountName: '',
      transitNumber: '',
      institutionNumber: '',
      accountNumber: '',
      bankName: '',
      voidChequePath: '',
    },
    validationSchema: getValidationSchema(selectedBank),
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (currentStep === 'form') {
        // Validate form and move to terms step
        const errors = await formik.validateForm();
        if (Object.keys(errors).length === 0) {
          setCurrentStep('terms');
          await fetchTerms();
        }
        return;
      }

      // Submit only if terms are accepted
      if (!termsAccepted) {
        showToast({
          message: 'Please accept the terms to continue',
          type: 'error',
        });
        return;
      }

      setLoading(true);
      try {
        const paymentOption = await createPaymentOption(auth?.data.token, {
          is3DS: false,
          tenantBankAccount: {
            accountName: values.accountName,
            transitNumber: values.transitNumber,
            institutionNumber: values.institutionNumber,
            accountNumber: values.accountNumber,
            bankName: values.bankName,
            voidChequePath: values.voidChequePath,
          },
          isDefault: false,
        });

        try {
          await authPayment(auth?.data.token, String(paymentOption?.id));
        } catch (authError) {
          console.error('Payment method verification (auth) failed:', authError);
        }

        await refreshPaymentOptions();
        showToast({
          message: 'Bank account added successfully!',
          type: 'success',
        });

        trackPaymentMethodAdded('bank');

        const tenantBuildingId = modalState?.tenantBuildingId;
        const payPlan = modalState?.feePaymentPlan === 'direct' ? 'direct' : 'autopay';
        setModal(null);
        if (tenantBuildingId) {
          navigate('/pay-rent', {
            state: {
              buildingId: tenantBuildingId,
              initialStep: 1,
              payPlan,
              syncPayFlowFromState: true,
            },
          });
        }
      } catch (error: any) {
        console.error('Error creating bank account:', error);
        showToast({
          message: error?.message || 'Failed to add bank account. Please try again.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    },
  });

  const replaceTermsVariables = (
    htmlContent: string,
    tenantName: string,
    companyName: string,
    authorizationDate: string
  ) => {
    return htmlContent
      .replace(/\{tenant_name\}/g, tenantName)
      .replace(/\{company_name\}/g, companyName)
      .replace(/\{authorization_date\}/g, authorizationDate);
  };

  const fetchTerms = async () => {
    try {
      setLoadingTerms(true);
      setHasScrolledToBottom(false);
      setTermsAccepted(false);

      // Fetch terms and tenant details in parallel
      const [termsResponse, tenantDetailsResponse] = await Promise.all([
        getPadTerms(auth?.data.token, 'authDirectPayment'),
        getTenantDetails(auth?.data.token),
      ]);

      // Parse the HTML content - handle both JSON string and regular string
      let htmlContent = termsResponse.value;
      // If it's a JSON string (starts and ends with quotes), parse it
      if (htmlContent.startsWith('"') && htmlContent.endsWith('"')) {
        try {
          htmlContent = JSON.parse(htmlContent);
        } catch {
          // If parsing fails, just remove outer quotes
          htmlContent = htmlContent.replace(/^"|"$/g, '');
        }
      }
      // Replace escaped characters
      htmlContent = htmlContent
        .replace(/\\r\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\n/g, '\n');

      // Get tenant information for variable replacement
      const tenantDetails = tenantDetailsResponse.data;
      const firstName = tenantDetails?.user?.firstName || '';
      const lastName = tenantDetails?.user?.lastName || '';
      const tenantName = `${firstName} ${lastName}`.trim() || 'User';
      const companyName = 'TenantPay'; // As per the template
      const authorizationDate = formatDateToDisplayUTCLong(new Date().toISOString());

      // Replace variables in the HTML
      htmlContent = replaceTermsVariables(htmlContent, tenantName, companyName, authorizationDate);

      setTermsContent(sanitizeHtml(htmlContent));

      // Check scroll position after content loads
      setTimeout(() => {
        checkScrollPosition();
      }, 100);
    } catch (error: any) {
      console.error('Error fetching PAD terms:', error);
      showToast({
        message: error?.message || 'Failed to load terms. Please try again.',
        type: 'error',
      });
    } finally {
      setLoadingTerms(false);
    }
  };

  const checkScrollPosition = () => {
    if (termsContentRef.current) {
      const element = termsContentRef.current;
      const needsScrolling = element.scrollHeight > element.clientHeight;

      // If content doesn't require scrolling (fits in container), allow acceptance
      if (!needsScrolling) {
        setHasScrolledToBottom(true);
        return;
      }
      // Check if scrolled to bottom (with 10px threshold for rounding)
      const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 10;
      setHasScrolledToBottom(isAtBottom);
    }
  };

  const handleTermsScroll = () => {
    checkScrollPosition();
  };

  const scrollTermsToBottom = () => {
    const element = termsContentRef.current;
    if (!element) return;

    element.scrollTo({
      top: element.scrollHeight,
      behavior: 'smooth',
    });
  };

  const handleTermsAcceptedChange = (checked: boolean) => {
    if (!hasScrolledToBottom) {
      scrollTermsToBottom();
      return;
    }
    setTermsAccepted(checked);
  };

  const handleBackToForm = () => {
    setCurrentStep('form');
    setTermsAccepted(false);
    setHasScrolledToBottom(false);
  };

  const handleTermsSubmit = () => {
    if (!termsAccepted) {
      showToast({
        message: 'Please accept the terms to continue',
        type: 'error',
      });
      return;
    }
    formik.handleSubmit();
  };

  return (
    <motion.main
      className={styles.main}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={styles.headingOuter}>
        <div className='flex items-center gap-[12px]'>
          <button
            onClick={() =>
              setModal('add-payment-method', {
                tenantBuildingId: modalState?.tenantBuildingId,
                payRentSetupFlow: modalState?.payRentSetupFlow,
                feePaymentPlan: modalState?.feePaymentPlan,
              })
            }
          >
            <ChevronLeft stroke='#0E0E0B' />
          </button>
          <span>Pre authorized debit</span>
        </div>
        {!payRentSetupFlow && (
          <X className={styles.crossIcon} size={24} onClick={() => setModal(null)} stroke='#0E0E0B' />
        )}
      </div>

      {currentStep === 'form' ? (
        <form
          onSubmit={formik.handleSubmit}
          className={styles.formOuter}
          data-clarity-mask='true'
        >
          {/* Account Name */}
          <div className={`${styles.item} inputWrapper`}>
            <label htmlFor='accountName' className='label'>
              Account name <span className={styles.required}>*</span>
            </label>
            <input
              id='accountName'
              type='text'
              placeholder='Account name'
              className='inputField'
              {...formik.getFieldProps('accountName')}
            />
            {formik.touched.accountName && formik.errors.accountName && (
              <div className={styles.error}>{formik.errors.accountName}</div>
            )}
          </div>

          {/* Bank Name */}
          <div className={`${styles.item} inputWrapper`}>
            <label htmlFor='bankName' className='label'>
              Bank name <span className={styles.required}>*</span>
            </label>
            {loadingBanks ? (
              <div className='inputFieldLoading' />
            ) : (
              <select
                id='bankName'
                className='inputField'
                value={selectedBank?.bankNameId || ''}
                onChange={(e) => {
                  handleBankChange(e.target.value);
                }}
              >
                <option value=''>Select a bank</option>
                {banks.map((bank) => (
                  <option key={bank.bankNameId} value={bank.bankNameId}>
                    {bank.name}
                  </option>
                ))}
              </select>
            )}
            {formik.touched.bankName && formik.errors.bankName && (
              <div className={styles.error}>{formik.errors.bankName}</div>
            )}
          </div>

          <div className={styles.formGrid}>
            {/* Institution Number */}
            <div className={`${styles.item} inputWrapper`}>
              <label htmlFor='institutionNumber' className='label'>
                Institution number <span className={styles.required}>*</span>
              </label>
              <input
                id='institutionNumber'
                type='text'
                placeholder='000'
                className='inputField'
                maxLength={3}
                disabled={
                  selectedBank?.institutionNumber !== null &&
                  !!selectedBank?.institutionNumber &&
                  selectedBank?.name !== 'Other'
                }
                {...formik.getFieldProps('institutionNumber')}
              />
              {formik.touched.institutionNumber && formik.errors.institutionNumber && (
                <div className={styles.error}>{formik.errors.institutionNumber}</div>
              )}
            </div>

            {/* Transit Number */}
            <div className={`${styles.item} inputWrapper`}>
              <label htmlFor='transitNumber' className='label'>
                Transit number <span className={styles.required}>*</span>
              </label>
              <input
                id='transitNumber'
                type='text'
                placeholder={
                  selectedBank?.transitNumberStartsWith
                    ? `Starts with ${selectedBank.transitNumberStartsWith}`
                    : selectedBank?.transitNumberFixedValue
                      ? selectedBank.transitNumberFixedValue
                      : '00000'
                }
                className='inputField'
                maxLength={5}
                disabled={!!selectedBank?.transitNumberFixedValue}
                {...formik.getFieldProps('transitNumber')}
              />
              {formik.touched.transitNumber && formik.errors.transitNumber && (
                <div className={styles.error}>{formik.errors.transitNumber}</div>
              )}
            </div>
          </div>

          {/* Account Number */}
          <div className={`${styles.item} inputWrapper`}>
            <label htmlFor='accountNumber' className='label'>
              Account number <span className={styles.required}>*</span>
            </label>
            <input
              id='accountNumber'
              type='text'
              placeholder={
                selectedBank
                  ? selectedBank.accountNumberMinLength === selectedBank.accountNumberMaxLength
                    ? `Account number (${selectedBank.accountNumberMinLength} digits)`
                    : `Account number (${selectedBank.accountNumberMinLength}-${selectedBank.accountNumberMaxLength} digits)`
                  : 'Account number'
              }
              className='inputField'
              maxLength={selectedBank?.accountNumberMaxLength || 18}
              {...formik.getFieldProps('accountNumber')}
            />
            {selectedBank && (
              <div className={styles.helpText}>
                {selectedBank.accountNumberFixedLengths.values.length > 0
                  ? `Must be exactly ${selectedBank.accountNumberFixedLengths.values.join(' or ')} digits`
                  : selectedBank.accountNumberMinLength === selectedBank.accountNumberMaxLength
                    ? `Must be exactly ${selectedBank.accountNumberMinLength} digits`
                    : `Must be between ${selectedBank.accountNumberMinLength} and ${selectedBank.accountNumberMaxLength} digits`}
              </div>
            )}
            {formik.touched.accountNumber && formik.errors.accountNumber && (
              <div className={styles.error}>{formik.errors.accountNumber}</div>
            )}
          </div>

          {/* Void Cheque Upload */}
          <div className={styles.item}>
            <label className='label'>Void cheque</label>
            <div className={styles.chequeExample}>
              <p className={styles.chequeExampleLabel}>Example of a void cheque:</p>
              <img
                src={canadaBankCheque}
                alt='Canada Bank Cheque Example'
                className={styles.chequeImage}
              />
            </div>
            <div
              className={`${styles.uploadOuter} ${selectedFile && styles.uploadedFile} ${
                !selectedFile && styles.uploadBox
              }`}
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }}
            >
              {!selectedFile && <ArrowUpFromLine stroke='var(--primary-color)' />}
              {!selectedFile && <div className={styles.textUpload}>Upload void cheque</div>}
              {selectedFile && (
                <div className={styles.uploadedFileInner}>
                  <FileText stroke='var(--primary-color)' />
                  <button
                    type='button'
                    className={styles.textUploaded}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(selectedFile, '_blank');
                    }}
                  >
                    {fileName}
                  </button>
                </div>
              )}
              {selectedFile && (
                <button
                  type='button'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedFile('');
                    setFileName('');
                    formik.setFieldValue('voidChequePath', '');
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                >
                  <Trash stroke='#f1416c' />
                </button>
              )}
            </div>
            {isUploading && <div className={styles.uploadingText}>Uploading...</div>}
            {formik.touched.voidChequePath && formik.errors.voidChequePath && (
              <div className={styles.error}>{formik.errors.voidChequePath}</div>
            )}
            <input
              type='file'
              ref={fileInputRef}
              onChange={handleFileChange}
              accept='image/*,.pdf'
              hidden
            />
          </div>

          <button
            type='submit'
            className={`${styles.submitBtn} ${styles.formSubmitBtn} ${(loading || isUploading || !formik.isValid) && styles.submitBtnDisabled}`}
            disabled={loading || isUploading || !formik.isValid}
          >
            {loading || isUploading ? (
              <div className='flex items-center justify-center gap-2'>
                <div className='w-4 h-4 border-2 border-[#E5E7EB] border-t-[#0055FF] rounded-full animate-spin' />
                <span>{isUploading ? 'Uploading...' : 'Processing...'}</span>
              </div>
            ) : (
              'Next'
            )}
          </button>
        </form>
      ) : (
        <div className={styles.formOuter}>
          <div className={styles.item}>
            <h2 className={styles.termsHeading}>Terms and conditions</h2>
            {loadingTerms ? (
              <div className={styles.loadingTerms}>Loading terms...</div>
            ) : (
              <div
                ref={termsContentRef}
                className={styles.termsContent}
                onScroll={handleTermsScroll}
                dangerouslySetInnerHTML={{ __html: termsContent }}
              />
            )}
          </div>

          <div className={styles.item}>
            <div
              className={`${styles.checkboxOuter} ${!hasScrolledToBottom && styles.checkboxDisabled}`}
            >
              <Checkbox
                checked={termsAccepted}
                onChange={handleTermsAcceptedChange}
              />
              <p className={styles.checkboxLabel}>
                I acknowledge and accept the Pre-Authorized Debit (PAD) Authorization terms
              </p>
            </div>
          </div>

          <div className='flex w-full gap-[12px]'>
            <button type='button' onClick={handleBackToForm} className='cancelBtn'>
              Back
            </button>
            <button
              type='button'
              onClick={handleTermsSubmit}
              className={`submitBtn ${(loading || !termsAccepted) && styles.submitBtnDisabled}`}
              disabled={loading || !termsAccepted}
            >
              {loading ? (
                <div className='flex items-center justify-center gap-2'>
                  <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin' />
                  <span>Adding...</span>
                </div>
              ) : (
                'Add bank account'
              )}
            </button>
          </div>
        </div>
      )}
    </motion.main>
  );
};

export default AddBankAccount;
